import { useEffect, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router';
import { fromCallback, fromPromise } from 'xstate';

import {
  CareType, getBetterhelpReferralDetails, getBetterhelpReferralDetailsByInviteToken, getBookingIntent, getMatchPreference, getSupportedAgeGroups,
  getUserContacts, getUserMatches, InsuranceMetaGrouping
} from '@uplift/api';
import { useCurrentuser } from '@uplift/contexts';
import { useStore } from '@uplift/hooks';
import { useInsuranceCompaniesQuery } from '@uplift/queries';
import { ProviderFilterTypeEnum, ProviderMedicalRole, ContactName } from '@uplift/types';
import { useMachine } from '@xstate/react';

import { LoadActorReturnType, BetterhelpOnboardingMachineContext } from '../types';
import { betterhelpOnboardingMachine } from './machine';

type Params = {
  invite_token?: string;
} | {
  profile_data?: string;
};

type UseOnboardingMachineProps = {
  isExiting: boolean;
};

const mergeInBookingIntent = async (context: Partial<BetterhelpOnboardingMachineContext>): Promise<Partial<BetterhelpOnboardingMachineContext>> => {
  const bookingIntent = await getBookingIntent().then(res => res?.data).catch(() => null);

  if (bookingIntent) {
    return {
      ...context,

      stateId: context.stateId ?? bookingIntent.stateId,
      insuranceId: context.insuranceId ?? bookingIntent.insuranceCompanyId,
      selectedProviderId: bookingIntent.providerId,
      appointment: bookingIntent.slotAvailable ? {
        timezone: bookingIntent.timezone,
        fromDatetime: bookingIntent.slotStart,
        toDatetime: bookingIntent.slotEnd,
        recurring: bookingIntent.recurring,
      } : null,
    };
  }

  return context;
};

const getMoreCurrentUserData = async (userId: string, isLoggedIn: boolean): Promise<Partial<BetterhelpOnboardingMachineContext>> => {
  if (!isLoggedIn || !userId) {
    return {};
  }

  const matches = await getUserMatches({
    userId,
    states: [ 'initial', 'active' ],
    forceMatchCreateIfMissing: true,
  });
  const initialMatch = matches.data.matches[0];
  const matchPreferences = initialMatch?.id ? await getMatchPreference(initialMatch?.id) : null;
  const contactsResponse = await getUserContacts(userId);
  const phoneVerified = contactsResponse.data.find(c => c.name === ContactName.mobile)?.verified;
  const selectedFilters: ProviderFilterTypeEnum[] = [];

  if (
    matchPreferences?.data?.faithIds?.length
    || matchPreferences?.data?.ethnicityIds?.length
    || matchPreferences?.data?.genderIds?.length
  ) {
    selectedFilters.push(ProviderFilterTypeEnum.Identity);
  }

  if (
    matchPreferences?.data?.communityExpertiseFaithIds?.length
    || matchPreferences?.data?.communityExpertiseEthnicityIds?.length
    || matchPreferences?.data?.communityExpertiseGeneralIds?.length
  ) {
    selectedFilters.push(ProviderFilterTypeEnum.Experience);
  }

  const ageGroupsResponse = await getSupportedAgeGroups();
  const ageGroup = ageGroupsResponse.ages.find(a => a.id === matchPreferences?.data?.ageGroupId);

  const contextData: Partial<BetterhelpOnboardingMachineContext> = await mergeInBookingIntent({
    matchId: initialMatch?.id,
    userId: userId,
    stateId: matchPreferences?.data?.locationState,
    seed: matchPreferences?.data?.seed,
    specialtyIds: matchPreferences?.data?.visitReasonIds ?? [],
    insuranceId: matchPreferences?.data?.insuranceMatch,
    modalityId: matchPreferences?.data?.modalityId,
    age: ageGroup ? { id: ageGroup.id, ageGroup: ageGroup.ageGroup } : null,
    identityFilters: {
      faithIds: matchPreferences?.data?.faithIds ?? [],
      ethnicityIds: matchPreferences?.data?.ethnicityIds ?? [],
      genderIds: matchPreferences?.data?.genderIds ?? [],
    },
    experienceFilters: {
      faithIds: matchPreferences?.data.communityExpertiseFaithIds,
      ethnicityIds: matchPreferences?.data.communityExpertiseEthnicityIds,
      communityIds: matchPreferences?.data.communityExpertiseGeneralIds,
    },
    showTheseProviderFilters: selectedFilters,
    phoneVerified: phoneVerified,
  });

  return contextData;
};

const getInviteDetails = async (inviteToken?: string, isLoggedIn?: boolean) => {
  if (isLoggedIn) {
    return await getBetterhelpReferralDetails().then(res => res.data).catch(() => null) ?? null;
  } else if (inviteToken) {
    // Must be allowed to reject: rejecting is how a spent or unknown token reaches
    // InvalidLink via Initial.onError. Catching here silently drops the user on Welcome.
    return (await getBetterhelpReferralDetailsByInviteToken(inviteToken)).data;
  }
  return null;
};

export const useBetterhelpOnboardingMachine = (props: UseOnboardingMachineProps) => {
  const { data: partnerInsuranceCompanies = [] } = useInsuranceCompaniesQuery();
  const { authStore } = useStore();
  const me = useCurrentuser();
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const params = Object.fromEntries(searchParams.entries()) as Params;

  const skipSnapshot = 'invite_token' in params || authStore.isLoggedIn;
  const snapshot = useMemo(() => skipSnapshot ? null : JSON.parse(localStorage.getItem('betterhelp-matching')), [ skipSnapshot ]);

  const machine = useMachine(betterhelpOnboardingMachine.provide({
    actors: {
      load: fromPromise(async (): Promise<LoadActorReturnType> => {
        try {
          const inviteToken = 'invite_token' in params ? params.invite_token : undefined;
          const contextData = await getMoreCurrentUserData(me.id, authStore.isLoggedIn);

          const context: Partial<BetterhelpOnboardingMachineContext> = {
            ...contextData,
            careType: CareType.psychiatry,
            isLoggedIn: authStore.isLoggedIn,
            inviteToken: inviteToken,
            inviteDetails: await getInviteDetails(inviteToken, authStore.isLoggedIn),
          };

          return {
            ...context,
            medicalRole: ProviderMedicalRole.psychiatrist,
          };
        } catch (err) {
          console.error(err);
          throw err;
        }
      }),
      handleHistoryPop: fromCallback(({ sendBack }) => {
        history.listen(() => {
          if (history.action === 'POP') {
            sendBack({ type: 'Back' });
          }
        });
      }),
      syncRouteActor: fromCallback(({ input }) => {
        history.push(input.route);
      })
    },
    guards: {
      isInsuranceMedicaid: ({ context, event }) => {
        let insuranceId = context.insuranceId;

        if (event.type === 'InsuranceInfo.Next' || event.type === 'Insurance.Next' || event.type === 'Matches.UpdateFilters') {
          insuranceId = event.data.insuranceId;
        }

        const insurance = partnerInsuranceCompanies.find(c => c.id === insuranceId);

        return insurance?.metaGrouping === InsuranceMetaGrouping.medicaid;
      }
    }
  }), {
    snapshot: snapshot,
  });

  const [ state, send, actor ] = machine;
  const persistedState = actor.getPersistedSnapshot();

  const { data: insuranceCompaniesForState } = useInsuranceCompaniesQuery({ stateId: state.context.stateId });
  const isPartnerInsurance = useMemo(() => (
    state.context.insuranceId && insuranceCompaniesForState
      ? insuranceCompaniesForState.some(company => company.id === state.context.insuranceId)
      : null
  ), [ state.context.insuranceId, insuranceCompaniesForState ]);

  useEffect(() => {
    if (props.isExiting) {
      return;
    }

    if (persistedState) {
      localStorage.setItem('betterhelp-matching', JSON.stringify(persistedState));
    }
  }, [ persistedState, props.isExiting ]);

  const triggeredRef = useRef(false);

  useEffect(() => {
    if (triggeredRef.current) {
      return;
    }

    if (!authStore.loading && !me.isLoading) {
      triggeredRef.current = true;
      send({ type: 'Start' });
    }
  }, [ authStore.loading, me.isLoading, send ]);

  return { isPartnerInsurance, state, send };
};
